/**
 * 단발 JSON POST — 헤더·base URL 은 대화형 채팅 transport 와 같은 재료(requestConfig)를 쓴다.
 * 은퇴 대상인 axios AI_HTTP_CLIENT(src/api/ai/client.ts)는 쓰지 않는다.
 */
import {
  createAiServerRequestHeaders,
  extractServerName,
  getConfiguredAiServerBaseUrl,
} from '@/api/ai/requestConfig';
import { ApiTokenStorage } from '@/module/auth/utils/ApiTokenStorage';
import { getClientFingerprintId } from '@/shared/utils/fingerprint';

import { QUESTION_CARD_AI_TIMEOUT_MS } from '../consts/questionCardAi.const';

import { toQuestionCardAiHttpError } from './questionCardAiHttpError';
import { withAbort } from './withAbort';

export type QuestionCardAiFetch = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

type MaybeAsync<T> = T | Promise<T>;

export type QuestionCardAiTransportDependencies = {
  baseUrl?: string;
  fetch?: QuestionCardAiFetch;
  getToken?: () => MaybeAsync<string | null | undefined>;
  getServerName?: () => MaybeAsync<string | null | undefined>;
  getFingerprintId?: () => MaybeAsync<string | null | undefined>;
};

/** 값이 undefined 인 최상위 키를 뺀다 — 서버 pydantic 이 null 과 미전송을 다르게 본다. */
const withoutUndefined = <T extends Record<string, unknown>>(body: T) =>
  Object.fromEntries(
    Object.entries(body).filter(([, value]) => value !== undefined),
  );

const resolve = async <T>(
  getter: (() => MaybeAsync<T>) | undefined,
  fallback: () => MaybeAsync<T>,
) => (getter ? await getter() : await fallback());

const createHeaders = async (
  dependencies: QuestionCardAiTransportDependencies,
): Promise<Headers> => {
  const headers = new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
  });

  const token = await resolve(dependencies.getToken, () =>
    ApiTokenStorage.get(),
  );
  const serverName = await resolve(dependencies.getServerName, () =>
    extractServerName(),
  );

  let fingerprintId: string | null | undefined;
  try {
    fingerprintId = await resolve(dependencies.getFingerprintId, () =>
      getClientFingerprintId(),
    );
  } catch {
    fingerprintId = undefined; // fingerprint 실패가 요청을 막지 않는다
  }

  Object.entries(
    createAiServerRequestHeaders({ token, serverName, fingerprintId }),
  ).forEach(([key, value]) => headers.set(key, value));

  return headers;
};

export const postQuestionCardAiJson = async <T>(
  path: string,
  body: Record<string, unknown>,
  signal: AbortSignal | undefined,
  dependencies: QuestionCardAiTransportDependencies,
): Promise<T> => {
  const fetchImpl = dependencies.fetch ?? globalThis.fetch;
  const baseUrl = (
    dependencies.baseUrl ??
    getConfiguredAiServerBaseUrl() ??
    ''
  ).replace(/\/$/, '');

  const abortSignal =
    signal ?? AbortSignal.timeout(QUESTION_CARD_AI_TIMEOUT_MS);
  // 헤더 준비(fingerprint 동적 로드 등)도 취소·타임아웃 경계 안에 둔다.
  const headers = await withAbort(createHeaders(dependencies), abortSignal);

  const response = await fetchImpl(`${baseUrl}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(withoutUndefined(body)),
    signal: abortSignal,
  });

  if (!response.ok) throw await toQuestionCardAiHttpError(response);

  return (await response.json()) as T;
};
